import { forwardRef, useImperativeHandle } from 'react';
import useAutoPlay from './use-auto-play';
import usePictureInPicture from './use-picture-in-picture';

interface PlayerProps {
  src: string;
  autoPlay?: boolean;
  loop?: boolean;
  playsInline?: boolean;
  muted?: boolean;
}

export type VideoPlayerRef = {
  play: () => Promise<void>;
  exitPictureInPicture: () => Promise<void> | void;
};

const VideoPlayer = forwardRef<VideoPlayerRef, PlayerProps>(
  ({ src, autoPlay, loop = false, playsInline = true, muted = true }, ref) => {
    const { ref: videoRef } = useAutoPlay({ autoPlay, src, muted });
    const { isPiP, exitPictureInPicture } = usePictureInPicture(videoRef);

    useImperativeHandle(
      ref,
      () => ({
        play: async () => await videoRef.current?.play(),
        exitPictureInPicture
      }),
      [exitPictureInPicture, videoRef]
    );

    return (
      <div
        className="relative z-10 flex items-center justify-center landscape:h-full portrait:w-full max-w-full max-h-full"
        onClick={e => e.stopPropagation()}>
        <video
          ref={videoRef}
          src={src}
          muted={muted || autoPlay}
          className={`max-w-full max-h-full object-scale-down ${isPiP ? 'opacity-0 pointer-events-none' : ''}`}
          preload="auto"
          controls
          controlsList="nodownload"
          playsInline={playsInline}
          webkit-playsinline={playsInline ? 'true' : 'false'}
          x5-playsinline={playsInline ? 'true' : 'false'}
          loop={loop}
        />
      </div>
    );
  }
);

export default VideoPlayer;
